import React from "react";
import { 
  AppBar, 
  Toolbar, 
  Box, 
  IconButton, 
  useMediaQuery, 
  Tooltip, 
  Chip,
  Avatar,
  Typography
} from "@mui/material";
import { Notifications, Menu as MenuIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { setActiveTabByPath } from "../../tabs/tabsSlice";
import logo from "../../assets/logostx.png";
import UserMenu from "./CustomUserProfile";
import { useAuth } from "../../auth/AuthProvider";
import { RBAC } from "../../services/api";
import { CompanySwitchMenu } from "./CompanySwitchMenu";

const resolveAssetBase = () => {
  const envBase = process.env.REACT_APP_API_BASE;
  const origin = (typeof window !== "undefined" && window.location && window.location.origin)
    ? window.location.origin
    : "";
  if (envBase) {
    try {
      return new URL(envBase, origin || undefined).origin;
    } catch {
      return origin;
    }
  }
  return origin;
};

const buildAssetUrl = (value) => {
  if (!value) return null;
  const raw = String(value).trim();
  if (!raw) return null;
  if (/^https?:\/\//i.test(raw) || raw.startsWith("data:") || raw.startsWith("blob:")) return raw;
  const base = resolveAssetBase();
  if (!base) return raw;
  const path = raw.startsWith("/") ? raw : `/${raw}`;
  return `${base}${path}`;
};

function AppBarComponent({ setDrawerOpen }) {
  const isMobile = useMediaQuery("(max-width:768px)");
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { company, refreshAuth, authVersion, loading } = useAuth();
  const [anchorEl, setAnchorEl] = React.useState(null);
  const [companies, setCompanies] = React.useState([]);
  const [imgError, setImgError] = React.useState(false);
  
  const logoSrc = buildAssetUrl(company?.logo) || logo;
  const displayName = company?.label || company?.name || "STX System";

  const handleHome = () => {
    navigate("/home");
    dispatch(setActiveTabByPath(null));
  };

  const openMenu = Boolean(anchorEl);
  
  const handleOpenCompanies = async (e) => {
    setAnchorEl(e.currentTarget);
    try {
      const { data } = await RBAC.userCompanies('me');
      const list = (Array.isArray(data) ? data : []).map(uc => ({
        id: uc.company?.id || uc.company_id,
        name: uc.company?.name || uc.company_name || 'Empresa',
        label: uc.company?.label || uc.company_label || null,
        current: !!uc.current
      }));
      setCompanies(list);
    } catch {
      setCompanies([]);
    }
  };
  
  const handleCloseCompanies = () => setAnchorEl(null);
  
  const handleSwitchCompany = async (id) => {
    try { 
      await RBAC.switchCompany(id); 
      await refreshAuth(); 
      setImgError(false); // Resetear error al cambiar de empresa
    } catch {}
    setAnchorEl(null);
  };

  const getInitials = () => {
    if (!displayName) return "STX";
    return displayName
      .split(" ")
      .map(word => word[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  if (loading) return null;

  return (
    <AppBar
      key={authVersion}
      position="fixed"
      sx={{
        background: (theme) => theme.palette.primary.main,
        boxShadow: 4,
        color: "white",
        zIndex: isMobile ? 1100 : 1300,
        height: 64,
      }}
    >
      <Toolbar sx={{ display: "flex", justifyContent: "space-between" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {isMobile && (
            <IconButton onClick={() => setDrawerOpen(true)} sx={{ color: "white" }}>
              <MenuIcon />
            </IconButton>
          )}
          <Box
            onClick={handleHome}
            sx={{
              position: "relative",
              height: "64px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
            }}
          >
            {/* Tirantes */}
            <Box
              sx={{
                position: "absolute",
                top: 0,
                left: "25%",
                width: "2px",
                height: "16px",
                backgroundColor: "#ccc",
                zIndex: 1,
              }}
            />
            <Box
              sx={{
                position: "absolute",
                top: 0,
                right: "25%",
                width: "2px",
                height: "16px",
                backgroundColor: "#ccc",
                zIndex: 1,
              }}
            />

            {/* Rótulo con logo o placeholder */}
            <Box
              sx={{
                backgroundColor: "white",
                borderRadius: "6px",
                padding: "4px 12px",
                boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                height: isMobile ? '35px' : '40px',
                minWidth: isMobile ? '80px' : '100px',
                opacity: 0,
                transform: "translateY(-60px) scale(0.95)",
                animation: "dropIn 1.2s ease-out forwards",
                "@keyframes dropIn": {
                  "0%": {
                    opacity: 0,
                    transform: "translateY(-60px) scale(0.95)",
                  },
                  "40%": {
                    opacity: 0.5,
                    transform: "translateY(-20px) scale(1.03)",
                  },
                  "70%": {
                    opacity: 1,
                    transform: "translateY(5px) scale(1.01)",
                  },
                  "100%": {
                    opacity: 1,
                    transform: "translateY(0) scale(1)",
                  },
                },
                transition: "transform 0.3s ease, box-shadow 0.3s ease",
                "&:hover": {
                  transform: "translateY(0) scale(1.06)",
                  boxShadow: "0 6px 18px rgba(0,0,0,0.3)",
                },
              }}
            >
              {/* Si hay logo y no hay error, mostrar imagen */}
              {logoSrc && !imgError ? (
                <Box
                  component="img"
                  src={logoSrc}
                  alt="Logo"
                  onError={() => setImgError(true)}
                  sx={{
                    height: "100%",
                    width: "auto",
                    objectFit: "contain",
                    maxWidth: "100%",
                  }}
                />
              ) : (
                // Placeholder "chilero" cuando no hay imagen
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1,
                  }}
                >
                  <Avatar
                    sx={{
                      bgcolor: (theme) => theme.palette.primary.main,
                      width: 28,
                      height: 28,
                      fontSize: '0.9rem',
                      fontWeight: 'bold',
                    }}
                  >
                    {getInitials()}
                  </Avatar>
                  {!isMobile && (
                    <Typography
                      variant="caption"
                      sx={{
                        color: (theme) => theme.palette.primary.main,
                        fontWeight: 700,
                        fontSize: '0.85rem',
                        textTransform: 'uppercase',
                        letterSpacing: '0.5px',
                      }}
                    >
                      {displayName.length > 12 
                        ? `${displayName.substring(0, 12)}...` 
                        : displayName}
                    </Typography>
                  )}
                </Box>
              )}
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
          {!!company && (
            <Tooltip title="Cambiar empresa">
              <Chip
                onClick={handleOpenCompanies}
                label={displayName}
                variant="outlined"
                sx={{
                  bgcolor: 'white', 
                  color: 'black', 
                  fontWeight: 600,
                  '&:hover': { 
                    bgcolor: (theme) => theme.palette.primary.light,
                    color: 'white' 
                  },
                }}
              />
            </Tooltip>
          )}
          <Tooltip title="Notificaciones">
            <IconButton sx={{ color: "white", '&:hover': { color: "#ff9800" } }}>
              <Notifications />
            </IconButton>
          </Tooltip>
          <UserMenu />
        </Box>
      </Toolbar>
      <CompanySwitchMenu
        anchorEl={anchorEl}
        openMenu={openMenu}
        handleCloseCompanies={handleCloseCompanies}
        companies={companies}
        handleSwitchCompany={handleSwitchCompany}
        loading={loading}
      />  
    </AppBar>
  );
}

export default AppBarComponent;
